import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import ToggleField from '@components/Common/ToggleField';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import React from 'react';

import GuideImagePicker from '../GuideImagePicker';
import { type FieldSpec, conforms, emptyValue, normaliseHeading } from './fieldMap';
import JsonField from './JsonField';
import RichTextField from './RichTextField';
import { useStructured } from './StructuredContext';

export interface FieldEditorProps {
  spec: FieldSpec;
  value: unknown;
  /** Dot path from the guide root. Also the input's id, so it must be unique on the page. */
  path: string;
  onChange: (next: unknown) => void;
  /** A top-level section prints its label in the section header, so the field does not repeat it. */
  hideLabel?: boolean;
}

const fieldName = (path: string) => `gf-${path}`;

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Move item `i` by `delta` places. Returns the same array when the move would leave the ends. */
export function move<T>(items: T[], i: number, delta: number): T[] {
  const j = i + delta;
  if (j < 0 || j >= items.length) return items;
  const next = [...items];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/** The inline message for one path: an error wins over a warning. */
function useIssue(path: string) {
  const { issues } = useStructured();
  const issue = issues.get(path);
  return {
    error: issue?.level === 'error' ? issue.message : undefined,
    warning: issue?.level === 'warning' ? issue.message : undefined,
  };
}

/** A message under a group (a list or a table) rather than under one input. */
const GroupMessage: React.FC<{ path: string }> = ({ path }) => {
  const { error, warning } = useIssue(path);
  const message = error || warning;
  if (!message) return null;
  return (
    <Typography className={`ge-msg ${error ? 'ge-msg--error' : 'ge-msg--warning'}`} role={error ? 'alert' : undefined}>
      {message}
    </Typography>
  );
};

/** Up, down and remove for one row of a list. Labelled by position so a screen reader can tell rows apart. */
const RowActions: React.FC<{
  label: string;
  index: number;
  count: number;
  onMove: (delta: number) => void;
  onRemove: () => void;
}> = ({ label, index, count, onMove, onRemove }) => (
  <Box className="ge-row-actions">
    <IconButton size="small" aria-label={`Move ${label} up`} disabled={index === 0} onClick={() => onMove(-1)}>
      <ArrowUpwardIcon fontSize="small" />
    </IconButton>
    <IconButton size="small" aria-label={`Move ${label} down`} disabled={index === count - 1} onClick={() => onMove(1)}>
      <ArrowDownwardIcon fontSize="small" />
    </IconButton>
    <IconButton size="small" aria-label={`Remove ${label}`} onClick={onRemove}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

/* ── Leaves ───────────────────────────────────────────────────────────────── */

const TextEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'text' }> }> = ({
  spec,
  value,
  path,
  onChange,
  hideLabel,
}) => {
  const { error, warning } = useIssue(path);
  const label = hideLabel ? '' : spec.label;
  if (spec.rich) {
    return (
      <RichTextField
        name={fieldName(path)}
        label={label || spec.label}
        value={(value as string) ?? ''}
        error={error}
        warning={warning}
        onChange={onChange}
      />
    );
  }
  return (
    <FormField
      required={!!spec.required}
      name={fieldName(path)}
      label={label}
      value={(value as string) ?? ''}
      multiline={spec.multiline}
      error={!!error}
      helperText={error || warning || spec.help}
      textFieldProps={{
        ...(spec.multiline ? { rows: undefined, minRows: 2 } : {}),
        ...(spec.inputType ? { type: spec.inputType } : {}),
        ...(label ? {} : { slotProps: { htmlInput: { 'aria-label': spec.label } } }),
      }}
      onChange={(_n, v) => onChange(String(v ?? ''))}
    />
  );
};

const NumberEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'number' }> }> = ({
  spec,
  value,
  path,
  onChange,
}) => {
  const { error, warning } = useIssue(path);
  return (
    <FormField required={false}
      name={fieldName(path)}
      label={spec.label}
      type="number"
      value={value as number | ''}
      error={!!error}
      helperText={error || warning || spec.help}
      textFieldProps={{ slotProps: { htmlInput: { step: spec.integer ? 1 : 'any' } } }}
      // FormField hands back '' for an empty box. Kept as '' so the field can be cleared and
      // retyped; validation flags it, and the site never receives a number it cannot draw.
      onChange={(_n, v) => onChange(v === '' ? '' : Number(v))}
    />
  );
};

const SelectEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'select' }> }> = ({
  spec,
  value,
  path,
  onChange,
}) => (
  <SelectFormField required={false}
    name={fieldName(path)}
    label={spec.label}
    value={(value as string) ?? ''}
    options={[{ value: '', label: 'Not set' }, ...spec.options]}
    helperText={spec.help}
    // Not set is an absent field, not an empty string the site would try to match.
    onChange={(_n, v) => onChange(v === '' ? undefined : String(v))}
  />
);

const MultiSelectEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'multiSelect' }> }> = ({
  spec,
  value,
  path,
  onChange,
}) => {
  const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
  const { warning } = useIssue(path);
  const groupId = `${fieldName(path)}-label`;
  return (
    <Box>
      <Typography id={groupId} className="ge-group-label">
        {spec.label}
        {spec.max ? ` (up to ${spec.max})` : ''}
      </Typography>
      <Box className="ge-chips" role="group" aria-labelledby={groupId}>
        {spec.options.map((o) => {
          const on = selected.has(o.value);
          return (
            <Chip
              key={o.value}
              label={o.label}
              clickable
              // The fill is in structured.scss, not color="primary": the theme's MuiChip override
              // pins the background, so a primary chip renders exactly like an unpressed one.
              variant={on ? 'filled' : 'outlined'}
              className={`ge-chip${on ? ' is-pressed' : ''}`}
              aria-pressed={on}
              onClick={() => {
                const next = new Set(selected);
                if (on) next.delete(o.value);
                else next.add(o.value);
                // Kept in the option order, so the stored value does not depend on click order.
                onChange(spec.options.map((x) => x.value).filter((v) => next.has(v)));
              }}
            />
          );
        })}
      </Box>
      {warning && <Typography className="ge-msg ge-msg--warning">{warning}</Typography>}
    </Box>
  );
};

const HeadingRefEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'headingRef' }> }> = ({
  spec,
  value,
  path,
  onChange,
}) => {
  const { headings } = useStructured();
  const { warning } = useIssue(path);
  const current = typeof value === 'string' ? value : '';
  const known = headings.some((h) => normaliseHeading(h) === normaliseHeading(current));
  const options = [
    { value: '', label: spec.emptyLabel },
    ...headings.map((h) => ({ value: h, label: h })),
    // A placement pointing at a heading that has since been renamed stays visible and selectable,
    // rather than the select silently showing the default and the next save erasing it.
    ...(current && !known ? [{ value: current, label: `${current} (no such section)` }] : []),
  ];
  // Matched loosely, as the site does, then shown as the heading's own spelling.
  const shown = known ? headings.find((h) => normaliseHeading(h) === normaliseHeading(current)) ?? current : current;
  return (
    <SelectFormField required={false}
      name={fieldName(path)}
      label={spec.label}
      value={shown}
      options={options}
      error={!!warning}
      helperText={warning || spec.help}
      onChange={(_n, v) => onChange(v === '' ? undefined : String(v))}
    />
  );
};

/**
 * An image slot: `{ src, alt }`, or absent.
 *
 * Reuses the body editor's picker, so an image added here goes through the same store, the same
 * size and format policy and the same "can a reader actually load it" check, and the alt text is
 * asked for before the image lands.
 */
const ImageEditor: React.FC<{
  value: unknown;
  path: string;
  label: string;
  onChange: (next: { src: string; alt: string } | undefined) => void;
  /** A figure is nothing without its image, so it has no Remove: the figure itself is removed. */
  removable?: boolean;
}> = ({ value, path, label, onChange, removable = true }) => {
  const { slug } = useStructured();
  const image = isObject(value) ? value : null;
  const src = typeof image?.src === 'string' ? image.src : '';
  const alt = typeof image?.alt === 'string' ? image.alt : '';
  const altIssue = useIssue(`${path}.alt`);
  const srcIssue = useIssue(`${path}.src`);

  return (
    <Box className="ge-image">
      <Typography className="ge-group-label">{label}</Typography>
      {src ? (
        <Box className="ge-image__row">
          <Box component="img" className="ge-image__thumb" src={src} alt={alt} />
          <Box className="ge-image__fields">
            <FormField
              required
              name={fieldName(`${path}.alt`)}
              label="Alt text"
              value={alt}
              error={!!altIssue.error}
              helperText={altIssue.error || 'What the image shows, for a reader who cannot see it.'}
              onChange={(_n, v) => onChange({ src, alt: String(v ?? '') })}
            />
            <Box className="ge-image__actions">
              <GuideImagePicker slug={slug} label="Replace image" onPicked={(p) => onChange({ src: p.url, alt: p.alt })} />
              {removable && (
                <Button size="small" color="error" variant="outlined" onClick={() => onChange(undefined)}>
                  Remove image
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <GuideImagePicker slug={slug} label="Add image" onPicked={(p) => onChange({ src: p.url, alt: p.alt })} />
          {srcIssue.error && <Typography className="ge-msg ge-msg--error">{srcIssue.error}</Typography>}
        </>
      )}
    </Box>
  );
};

/* ── Collections ──────────────────────────────────────────────────────────── */

const StringListEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'stringList' }> }> = ({
  spec,
  value,
  path,
  onChange,
  hideLabel,
}) => {
  const items = Array.isArray(value) ? (value as string[]) : [];
  return (
    <Box className="ge-list">
      {!hideLabel && <Typography className="ge-group-label">{spec.label}</Typography>}
      {items.map((item, i) => {
        const label = `${spec.itemLabel} ${i + 1}`;
        return (
          <Box key={i} className="ge-list__row">
            <FormField
              required={false}
              multiline
              name={fieldName(`${path}.${i}`)}
              label=""
              value={item}
              textFieldProps={{ slotProps: { htmlInput: { 'aria-label': label } } }}
              onChange={(_n, edited) => {
                const next = [...items];
                next[i] = String(edited ?? '');
                onChange(next);
              }}
            />
            <RowActions
              label={label}
              index={i}
              count={items.length}
              onMove={(d) => onChange(move(items, i, d))}
              onRemove={() => onChange(items.filter((_, j) => j !== i))}
            />
          </Box>
        );
      })}
      <GroupMessage path={path} />
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={spec.max !== undefined && items.length >= spec.max}
        onClick={() => onChange([...items, ''])}
      >
        Add {spec.itemLabel.toLowerCase()}
      </Button>
    </Box>
  );
};

const ObjectEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'object' }> }> = ({
  spec,
  value,
  path,
  onChange,
}) => {
  const current = isObject(value) ? value : {};
  const set = (key: string, next: unknown) => {
    const out = { ...current };
    // Undefined removes the key: an optional slot cleared is an absent field, not a null the
    // template would have to guard.
    if (next === undefined) delete out[key];
    else out[key] = next;
    onChange(out);
  };
  const hidden = new Set(spec.hidden ?? []);
  const known = new Set(Object.keys(spec.fields));
  // Keys the map does not know are kept and shown as JSON, never dropped.
  const unknown = Object.keys(current).filter(
    (k) => !known.has(k) && !hidden.has(k) && !(spec.inlineImage && (k === 'src' || k === 'alt')),
  );

  return (
    <Box className="ge-object">
      {spec.inlineImage && (
        <ImageEditor
          value={current}
          path={path}
          label="Image"
          removable={false}
          onChange={(img) => onChange({ ...current, src: img?.src ?? '', alt: img?.alt ?? '' })}
        />
      )}
      {Object.entries(spec.fields).map(([key, child]) => (
        <FieldEditor key={key} spec={child} value={current[key]} path={`${path}.${key}`} onChange={(n) => set(key, n)} />
      ))}
      {unknown.map((key) => (
        <JsonField
          key={key}
          name={fieldName(`${path}.${key}`)}
          label={key}
          value={current[key]}
          note="The editor has no form for this field, so it is edited as JSON."
          onChange={(n) => set(key, n)}
        />
      ))}
    </Box>
  );
};

const ListEditor: React.FC<FieldEditorProps & { spec: Extract<FieldSpec, { kind: 'list' }> }> = ({
  spec,
  value,
  path,
  onChange,
  hideLabel,
}) => {
  const items = Array.isArray(value) ? (value as unknown[]) : [];
  const atMax = spec.max !== undefined && items.length >= spec.max;
  return (
    <Box className="ge-items">
      {!hideLabel && <Typography className="ge-group-label">{spec.label}</Typography>}
      {items.map((item, i) => {
        const label = `${spec.itemLabel} ${i + 1}`;
        const title =
          spec.titleKey && isObject(item) && typeof item[spec.titleKey] === 'string'
            ? String(item[spec.titleKey]).trim()
            : '';
        return (
          <Box key={i} className="ge-item" role="group" aria-label={label}>
            <Box className="ge-item__head">
              <Typography className="ge-item__title">
                {label}
                {title ? <span className="ge-item__name">{title}</span> : null}
              </Typography>
              <RowActions
                label={label}
                index={i}
                count={items.length}
                onMove={(d) => onChange(move(items, i, d))}
                onRemove={() => onChange(items.filter((_, j) => j !== i))}
              />
            </Box>
            <FieldEditor
              spec={spec.item}
              value={item}
              path={`${path}.${i}`}
              hideLabel
              onChange={(n) => {
                const next = [...items];
                next[i] = n;
                onChange(next);
              }}
            />
          </Box>
        );
      })}
      <GroupMessage path={path} />
      <Button size="small" startIcon={<AddIcon />} disabled={atMax} onClick={() => onChange([...items, emptyValue(spec.item)])}>
        Add {spec.itemLabel.toLowerCase()}
      </Button>
    </Box>
  );
};

/**
 * A ResourceTable: heading, note, columns and a grid of rows, plus where it sits on the page.
 *
 * Columns and rows are edited together, because they have to agree: adding a column adds a cell to
 * every row and removing one removes that cell from every row, so the grid never drifts into the
 * ragged shape the site renders with gaps.
 */
export const TableEditor: React.FC<FieldEditorProps> = ({ value, path, onChange }) => {
  const table = isObject(value) ? value : {};
  const columns = Array.isArray(table.columns) ? (table.columns as string[]) : [];
  const rows = Array.isArray(table.rows) ? (table.rows as string[][]) : [];
  const set = (patch: Record<string, unknown>) => {
    const out = { ...table, ...patch };
    for (const [k, v] of Object.entries(patch)) if (v === undefined) delete out[k];
    onChange(out);
  };
  const padded = (row: string[]) => columns.map((_, c) => row[c] ?? '');
  const ragged = rows.some((r) => r.length !== columns.length);

  return (
    <Box className="ge-table">
      <FormField required={false}
        name={fieldName(`${path}.heading`)}
        label="Table heading"
        value={(table.heading as string) ?? ''}
        onChange={(_n, v) => set({ heading: String(v ?? '') })}
      />
      <FormField required={false}
        name={fieldName(`${path}.note`)}
        label="Note"
        value={(table.note as string) ?? ''}
        helperText="Printed under the table, e.g. a source or a date."
        onChange={(_n, v) => set({ note: String(v ?? '') || undefined })}
      />
      <FieldEditor
        spec={{ kind: 'headingRef', label: 'Place after section', emptyLabel: 'With the other tables' }}
        value={table.afterSection}
        path={`${path}.afterSection`}
        onChange={(n) => set({ afterSection: n })}
      />

      <Box className="ge-table__scroll">
        <Box
          className="ge-table__grid"
          role="table"
          aria-label={(table.heading as string) || 'Table'}
          sx={{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(140px, 1fr)) auto` }}
        >
          <Box role="row" className="ge-table__row">
            {columns.map((col, c) => (
              <Box role="columnheader" key={c} className="ge-table__cell ge-table__cell--head">
                <FormField required={false}
                  name={fieldName(`${path}.columns.${c}`)}
                  label=""
                  value={col}
                  textFieldProps={{ slotProps: { htmlInput: { 'aria-label': `Column ${c + 1} header` } } }}
                  onChange={(_n, v) => {
                    const next = [...columns];
                    next[c] = String(v ?? '');
                    set({ columns: next });
                  }}
                />
                <IconButton
                  size="small"
                  aria-label={`Remove column ${c + 1}`}
                  disabled={columns.length <= 1}
                  onClick={() =>
                    set({
                      columns: columns.filter((_, j) => j !== c),
                      rows: rows.map((r) => padded(r).filter((_, j) => j !== c)),
                    })
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box className="ge-table__cell ge-table__cell--actions">
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => set({ columns: [...columns, `Column ${columns.length + 1}`], rows: rows.map((r) => [...padded(r), '']) })}
              >
                Column
              </Button>
            </Box>
          </Box>

          {rows.map((row, r) => (
            <Box role="row" key={r} className="ge-table__row">
              {columns.map((_, c) => (
                <Box role="cell" key={c} className="ge-table__cell">
                  <FormField required={false}
                    name={fieldName(`${path}.rows.${r}.${c}`)}
                    label=""
                    multiline
                    value={row[c] ?? ''}
                    textFieldProps={{ slotProps: { htmlInput: { 'aria-label': `Row ${r + 1}, ${columns[c] || `column ${c + 1}`}` } } }}
                    onChange={(_n, v) => {
                      const next = rows.map((x) => [...x]);
                      next[r] = padded(next[r]);
                      next[r][c] = String(v ?? '');
                      set({ rows: next });
                    }}
                  />
                </Box>
              ))}
              <Box role="cell" className="ge-table__cell ge-table__cell--actions">
                <RowActions
                  label={`row ${r + 1}`}
                  index={r}
                  count={rows.length}
                  onMove={(d) => set({ rows: move(rows, r, d) })}
                  onRemove={() => set({ rows: rows.filter((_, j) => j !== r) })}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {ragged && (
        <Box className="ge-table__ragged">
          <Typography className="ge-msg ge-msg--warning">
            Some rows do not have one cell per column, so the table renders with gaps.
          </Typography>
          <Button size="small" onClick={() => set({ rows: rows.map(padded) })}>
            Fit rows to columns
          </Button>
        </Box>
      )}
      <Button size="small" startIcon={<AddIcon />} onClick={() => set({ rows: [...rows, columns.map(() => '')] })}>
        Add row
      </Button>
    </Box>
  );
};

/* ── Dispatch ─────────────────────────────────────────────────────────────── */

/**
 * The editor for one field, chosen by its spec.
 *
 * A value whose shape does not match its spec (a string where a list belongs, a number where an
 * object belongs) is edited as JSON for that field alone, so a malformed corner of one guide never
 * costs the form for the rest of it, and the editor never writes the "expected" shape over content
 * it does not understand.
 */
const FieldEditor: React.FC<FieldEditorProps> = (props) => {
  const { spec, value, path, onChange } = props;
  if (!conforms(spec, value)) {
    return (
      <JsonField
        name={fieldName(path)}
        label={props.hideLabel ? '' : spec.label}
        value={value}
        note={`This value is not the shape the ${spec.label.toLowerCase()} editor expects, so it is edited as JSON.`}
        onChange={onChange}
      />
    );
  }
  switch (spec.kind) {
    case 'text':
      return <TextEditor {...props} spec={spec} />;
    case 'number':
      return <NumberEditor {...props} spec={spec} />;
    case 'select':
      return <SelectEditor {...props} spec={spec} />;
    case 'multiSelect':
      return <MultiSelectEditor {...props} spec={spec} />;
    case 'boolean':
      return <ToggleField label={spec.label} checked={value === true} onChange={(c) => onChange(c)} />;
    case 'headingRef':
      return <HeadingRefEditor {...props} spec={spec} />;
    case 'image':
      return <ImageEditor value={value} path={path} label={spec.label} onChange={onChange} />;
    case 'stringList':
      return <StringListEditor {...props} spec={spec} />;
    case 'object':
      return <ObjectEditor {...props} spec={spec} />;
    case 'list':
      return <ListEditor {...props} spec={spec} />;
    case 'table':
      return <TableEditor {...props} />;
    default:
      return <JsonField name={fieldName(path)} value={value} onChange={onChange} />;
  }
};

export default FieldEditor;
